import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { authApiClient } from '../features/auth/api/authApiClient';
import { useAuthStore } from '../features/auth/store';
import { getDefaultRouteForRole } from '../features/auth/permissions';
import { handleApiError } from '../shared/utils/handleApiError';
import { PasswordInput } from '../features/auth/components/ui/PasswordInput';
import Button from '../shared/components/Button';
import { Logo } from '../shared/components/Logo';
import { ThemeToggle } from '../shared/components/ThemeToggle';

// Mirrors watchman's CompletePasswordUpdateDto strength rules exactly (min 8 / max 64,
// lower + upper + digit + special from the [!@#$%^&*] set) so a valid form never trips a 422.
const rules = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 && p.length <= 64 },
  { label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One number', test: (p: string) => /\d/.test(p) },
  { label: 'One special character (!@#$%^&*)', test: (p: string) => /[!@#$%^&*]/.test(p) },
];

interface ForcePasswordUpdateState {
  email?: string;
  oldPassword?: string;
}

/**
 * PE-1667 / PE-2097 — forced password update on first login.
 *
 * Reached only via redirect from the login form when watchman returns
 * PASSWORD_UPDATE_REQUIRED (account still on a watchman-issued temporary password).
 * The email + temporary password arrive in router state (never persisted); a direct
 * visit without that state has nothing to submit, so we bounce back to /login.
 */
export const ForcePasswordUpdatePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const { email, oldPassword } = (location.state as ForcePasswordUpdateState | null) ?? {};

  const [form, setForm] = useState({ password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // No credentials to work with — send them back to sign in.
  if (!email || !oldPassword) return <Navigate to="/login" replace />;

  const passed = rules.filter((r) => r.test(form.password)).length;
  const strength = Math.min(passed, 4);
  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColor = ['', 'bg-red-400', 'bg-amber-400', 'bg-blue-400', 'bg-green-500'][strength];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (passed < rules.length) { setError('Password does not meet all requirements'); return; }
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      const { user, token, twoFactorEnabled } = await authApiClient.completePasswordUpdate({
        email,
        oldPassword,
        newPassword: form.password,
      });
      await login(user, token, false, twoFactorEnabled);
      navigate(getDefaultRouteForRole(user.roleName), { replace: true });
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col items-center justify-center px-4 py-10">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md flex flex-col items-center">
        <Logo className="h-9 w-auto mb-8" />

        <div className="w-full rounded-2xl border border-border bg-card text-card-foreground shadow-sm px-6 py-8 sm:px-8">
          <div className="mb-6 text-center">
            <h1 className="text-[24px] font-bold text-foreground mb-2">Update your password</h1>
            <p className="text-sm text-muted-foreground">
              You&apos;re signing in for the first time with a temporary password. Set a new password
              to continue to your dashboard.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Email</label>
              <input
                type="email"
                value={email}
                disabled
                className="w-full px-3 h-[42px] border border-border bg-muted text-foreground rounded-md text-sm cursor-not-allowed opacity-70"
              />
            </div>

            <div>
              <PasswordInput
                name="password"
                label="New Password"
                placeholder="Enter a strong password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                required
              />
              {form.password && (
                <div className="mt-2 space-y-1.5">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= strength ? strengthColor : 'bg-muted'}`} />
                    ))}
                  </div>
                  <span className={`text-[12px] font-[600] ${['', 'text-red-500', 'text-amber-500', 'text-blue-500', 'text-green-600'][strength]}`}>
                    {strengthLabel}
                  </span>
                  <ul className="space-y-1 mt-1">
                    {rules.map((rule) => (
                      <li key={rule.label} className={`flex items-center gap-1.5 text-[12px] ${rule.test(form.password) ? 'text-green-600' : 'text-muted-foreground'}`}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          {rule.test(form.password)
                            ? <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            : <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />}
                        </svg>
                        {rule.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <PasswordInput
              name="confirm"
              label="Confirm Password"
              placeholder="Re-enter your password"
              value={form.confirm}
              onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
              error={form.confirm && form.password !== form.confirm ? 'Passwords do not match' : undefined}
              required
            />

            {error && <p className="text-destructive text-[13px]">{error}</p>}

            <Button type="submit" loading={loading} className="w-full mt-2">
              Update Password &amp; Continue
            </Button>
          </form>
        </div>

        <p className="mt-6 text-xs text-muted-foreground text-center">
          The Collective Synergy &middot; Admin
        </p>
      </div>
    </div>
  );
};
